/**
 * 文档与图层模型（DESIGN.md §4.2）。
 *
 * 这一层是纯**数据形状**，不含行为：所有修改都经 Command Bus（M1-07），
 * 所以这里的每一个字段都是 `readonly`。这不是风格洁癖——CLAUDE.md 规则 5
 * 要求"任何地方直接 mutate Document 都是 bug"，而把它写进类型是唯一能
 * 在写出那行代码时就拦住的办法，代码评审拦不住。
 *
 * 两条贯穿全文件的约定：
 *
 * 1. **图层不持有像素。** RasterLayer 只有 `TileHandle`，类型层面就拿不到
 *    `ArrayBuffer`。像素归 TileStore 统一管，写时复制、驱逐、撤销才能集中实现。
 * 2. **所有坐标字段都注明坐标空间。** 文档空间、图层局部空间、屏幕空间三者
 *    混用是这类程序最常见的错误来源，而且往往表现为"偏移了几个像素"这种
 *    看得见却查不出的问题。
 */
import type { TileHandle } from '../engine/tile/store';
import type { LayerId } from '../engine/tile/key';

export type { LayerId };

/* ------------------------------------------------------------------ */
/* 几何                                                                 */
/* ------------------------------------------------------------------ */

/**
 * 3×3 仿射矩阵，行主序：
 * ```
 * | a c e |
 * | b d f |
 * | 0 0 1 |
 * ```
 * 与 CSS/Canvas2D 的 `matrix(a,b,c,d,e,f)` 一致，便于与浏览器 API 互操作。
 */
export interface Mat3 {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  /** 平移 x，单位：**目标空间的像素**。 */
  readonly e: number;
  /** 平移 y，单位：**目标空间的像素**。 */
  readonly f: number;
}

export const IDENTITY: Mat3 = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

/** 矩形。所有字段单位为像素，坐标空间由使用处的 TSDoc 说明。 */
export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export const EMPTY_RECT: Rect = { x: 0, y: 0, width: 0, height: 0 };

/* ------------------------------------------------------------------ */
/* 色彩                                                                 */
/* ------------------------------------------------------------------ */

/**
 * 工作色彩空间。内部统一按**线性光**处理（§4.2）。
 *
 * `cmyk-import` 不是工作空间，只是标记"这份文档从 CMYK 导入"——
 * §2.2 明确不做完整 CMYK 编辑，导入后转到 RGB 工作空间，
 * 保留原始数据仅用于导出时尽量还原。
 */
export type ColorMode = 'rgb' | 'gray' | 'cmyk-import';

export type BitDepth = 8 | 16 | 32;

export interface ColorSpace {
  /** ICC profile 名称，如 'sRGB'、'Display P3'。 */
  readonly name: string;
  /** 原始 ICC 字节。保留它是为了导出时能原样写回（§6.4）。 */
  readonly iccBytes?: Uint8Array;
  /** 是否为线性传输函数。合成必须在线性空间进行。 */
  readonly linear: boolean;
}

export const SRGB_LINEAR: ColorSpace = { name: 'sRGB (linear)', linear: true };

/* ------------------------------------------------------------------ */
/* 混合与效果                                                            */
/* ------------------------------------------------------------------ */

/**
 * 混合模式。Photoshop 完整 27 种。前 21 种是**可分离**的（逐通道独立
 * 计算，"溶解"归入这一组——它的逐通道颜色公式跟 normal 完全一样，
 * 真正的颗粒抖动发生在颜色公式之外的覆盖度计算上），后 6 种非可分离
 * （需要整个 RGB 一起算，"深色"/"浅色"要比较整像素明度）——这个分界
 * 决定了着色器怎么写，见 gpu/shaders/blend/{modes,nonseparable}.ts。
 *
 * 顺序追加而非按 PS 菜单分组排列：GPU 侧 `ALL_BLEND_MODES` 的序号一旦
 * 排定就是着色器与 CPU 之间的约定，插队会让已保存文档的混合模式整体
 * 错位（见 gpu/shaders/blend/generate.ts 的同一条约束）。UI 下拉菜单
 * 自己按 PS 习惯的分组顺序重排 `BLEND_MODE_OPTIONS`，不影响这里的顺序。
 */
export type BlendMode =
  // 可分离（含"溶解"）
  | 'normal'
  | 'dissolve'
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'darken'
  | 'lighten'
  | 'colorDodge'
  | 'colorBurn'
  | 'hardLight'
  | 'softLight'
  | 'difference'
  | 'exclusion'
  | 'linearBurn'
  | 'linearDodge'
  | 'vividLight'
  | 'linearLight'
  | 'pinLight'
  | 'hardMix'
  | 'subtract'
  | 'divide'
  // 非可分离
  | 'hue'
  | 'saturation'
  | 'color'
  | 'luminosity'
  | 'darkerColor'
  | 'lighterColor';

export const SEPARABLE_BLEND_MODES = [
  'normal',
  'dissolve',
  'multiply',
  'screen',
  'overlay',
  'darken',
  'lighten',
  'colorDodge',
  'colorBurn',
  'hardLight',
  'softLight',
  'difference',
  'exclusion',
  'linearBurn',
  'linearDodge',
  'vividLight',
  'linearLight',
  'pinLight',
  'hardMix',
  'subtract',
  'divide',
] as const satisfies readonly BlendMode[];

export const NON_SEPARABLE_BLEND_MODES = [
  'hue',
  'saturation',
  'color',
  'luminosity',
  'darkerColor',
  'lighterColor',
] as const satisfies readonly BlendMode[];

export interface LayerLocks {
  /** 锁定全部：任何修改都不允许。 */
  readonly all: boolean;
  /** 锁定透明像素：只能画在已有内容上。 */
  readonly transparency: boolean;
  readonly position: boolean;
}

export const NO_LOCKS: LayerLocks = { all: false, transparency: false, position: false };

/**
 * 图层效果，Photoshop 完整 10 种（DESIGN.md §7.1 补充）。`params` 是宽松
 * 的 Record——每种效果具体认哪些字段、缺省值多少，由
 * `engine/effects/resolve.ts` 的 `resolveEffect` 按 kind 解析，跟调整图层
 * 的参数解析（`engine/adjustments/resolve.ts`）同一套鸭子类型策略。
 */
export interface LayerEffect {
  readonly kind:
    | 'dropShadow'
    | 'innerShadow'
    | 'outerGlow'
    | 'innerGlow'
    | 'bevelEmboss'
    | 'satin'
    | 'colorOverlay'
    | 'gradientOverlay'
    | 'patternOverlay'
    | 'stroke';
  readonly enabled: boolean;
  readonly params: Readonly<Record<string, number | string>>;
}

/** 图层效果种类的字面量集合，供 UI 穷尽列出与测试用。 */
export const LAYER_EFFECT_KINDS = [
  'dropShadow',
  'innerShadow',
  'outerGlow',
  'innerGlow',
  'bevelEmboss',
  'satin',
  'colorOverlay',
  'gradientOverlay',
  'patternOverlay',
  'stroke',
] as const satisfies readonly LayerEffect['kind'][];

/** 混合条件（Blend If）。取值 0–255，对应通道的输入/输出范围。 */
export interface BlendIfRange {
  readonly channel: 'gray' | 'red' | 'green' | 'blue';
  readonly sourceMin: number;
  readonly sourceMax: number;
  readonly targetMin: number;
  readonly targetMax: number;
}

/* ------------------------------------------------------------------ */
/* 蒙版与路径                                                            */
/* ------------------------------------------------------------------ */

/**
 * 栅格蒙版。
 *
 * 与图层像素一样只持有 `TileHandle`——蒙版也是像素，同样归 TileStore 管，
 * 否则撤销与驱逐要为它单独实现一套。
 */
export interface RasterMask {
  readonly tiles: TileHandle;
  /** 有内容的最小外接矩形，**文档空间**像素。 */
  readonly bounds: Rect;
  /** 蒙版是否随图层一起变换。取消链接后可单独移动蒙版。 */
  readonly linked: boolean;
  readonly enabled: boolean;
  /**
   * 未分配块的蒙版值（与 SelectionMask 同一套稀疏语义，M2-06/M2-13）：
   * 255=显示（默认「全部显示」蒙版零分配），0=隐藏。缺省按 255。
   */
  readonly base?: 0 | 255;
}

/** 路径数据。坐标在**图层局部空间**，随图层 transform 变换。 */
export interface PathData {
  /** SVG path 语法的子集：M/L/C/Q/Z，绝对坐标。 */
  readonly commands: string;
  readonly fillRule: 'nonzero' | 'evenodd';
}

/* ------------------------------------------------------------------ */
/* 图层                                                                 */
/* ------------------------------------------------------------------ */

export interface LayerBase {
  readonly id: LayerId;
  readonly name: string;
  readonly visible: boolean;
  /** 0–1。作用于图层与其效果的整体。 */
  readonly opacity: number;
  /**
   * 0–1。**只影响像素，不影响图层样式**——这是它与 opacity 的全部区别，
   * 也是很多实现搞错的地方（§4.4 的八步合成顺序里两者位置不同）。
   */
  readonly fillOpacity: number;
  readonly blendMode: BlendMode;
  /** 剪贴到下方图层。 */
  readonly clipped: boolean;
  readonly locks: LayerLocks;
  /** 图层局部空间 → **文档空间**的仿射变换。 */
  readonly transform: Mat3;
  readonly mask?: RasterMask;
  readonly vectorMask?: PathData;
  readonly effects: readonly LayerEffect[];
  readonly blendIf?: BlendIfRange;
}

/**
 * 像素图层。
 *
 * **不持有像素**：`tiles` 是 TileHandle，类型层面就拿不到 ArrayBuffer。
 * 这条由 test/document/draft.test.ts 的类型级断言守着。
 */
export interface RasterLayer extends LayerBase {
  readonly kind: 'raster';
  readonly tiles: TileHandle;
  /** 有内容的最小外接矩形，**文档空间**像素。 */
  readonly bounds: Rect;
}

export interface GroupLayer extends LayerBase {
  readonly kind: 'group';
  readonly children: readonly Layer[];
  /**
   * 穿透模式：组内的调整图层影响组外。
   *
   * 非穿透时组先独立合成再整体混合——两者的像素结果不同，
   * 不是性能优化而是语义差别（§4.4）。
   */
  readonly passThrough: boolean;
  readonly collapsed: boolean;
}

/** 文本图层。排版结果由 harfbuzz 产出（尖刺三已验证管线）。 */
export interface TextLayer extends LayerBase {
  readonly kind: 'text';
  readonly runs: readonly TextRun[];
  /** 文本框，**图层局部空间**。点文本时宽高为 0。 */
  readonly box: Rect;
  readonly writingMode: 'horizontal' | 'vertical';
  /**
   * 文字变形（对比 Photoshop 补全第一档缺口 阶段 5）。只作用于横排
   * （`writingMode: 'vertical'` 时忽略——竖排文字变形不在这次范围内）。
   */
  readonly warp?: { readonly style: 'arc' | 'bulge' | 'wave'; readonly bend: number };
}

/**
 * 矢量取色：纯色或渐变——形状图层的填充/描边、文字的填充/描边共用
 * 这一份定义。渐变几何固定为"从图层自身外接矩形左边到右边的水平线性
 * 渐变"（不像 FillLayer.fill 或渐变工具那样能拖任意方向/起止点），
 * 换来的是形状/文字属性栏不需要再接一套方向手柄——色标够用，方向留死。
 */
export type VectorFill =
  | { readonly type: 'solid'; readonly color: string; readonly alpha?: number }
  | { readonly type: 'gradient'; readonly stops: readonly { readonly offset: number; readonly color: string }[] };

/** 矢量描边：取色方式 + 宽度，跨在原路径边界两侧（Photoshop 的「居中」对齐）。 */
export interface VectorStroke {
  readonly fill: VectorFill;
  readonly width: number;
}

export interface TextRun {
  readonly text: string;
  readonly fontFamily: string;
  /** 字号，**图层局部空间**的像素。 */
  readonly fontSize: number;
  readonly fill: VectorFill;
  readonly stroke?: VectorStroke;
  /** 字距，单位为 1/1000 em。中文排版应保持 0（§7.3）。 */
  readonly letterSpacing: number;
  readonly lineHeight: number;
}

export interface ShapeLayer extends LayerBase {
  readonly kind: 'shape';
  readonly path: PathData;
  readonly fill?: VectorFill;
  readonly stroke?: VectorStroke;
}

/** 调整参数。纯参数节点，没有像素——非破坏性编辑的基础（§4.2）。 */
export interface Adjustment {
  readonly kind:
    | 'curves'
    | 'levels'
    | 'hsl'
    | 'colorBalance'
    | 'lut'
    | 'brightnessContrast'
    | 'basic'
    | 'blackAndWhite'
    | 'photoFilter'
    | 'channelMixer'
    | 'gradientMap'
    | 'posterize'
    | 'threshold'
    | 'vibrance';
  readonly params: Readonly<Record<string, number | readonly number[]>>;
}

export interface AdjustmentLayer extends LayerBase {
  readonly kind: 'adjustment';
  readonly adjustment: Adjustment;
}

/** 填充图层。同样没有像素。 */
export interface FillLayer extends LayerBase {
  readonly kind: 'fill';
  readonly fill:
    | { readonly type: 'solid'; readonly color: string }
    | { readonly type: 'gradient'; readonly stops: readonly { offset: number; color: string }[] }
    | { readonly type: 'pattern'; readonly patternId: string };
}

/** 智能对象的来源：原始文件字节，或内嵌的完整文档。 */
export type EmbeddedSource =
  | { readonly type: 'file'; readonly bytes: Uint8Array; readonly mime: string }
  | { readonly type: 'document'; readonly document: Document };

export interface SmartFilter {
  readonly kind: string;
  readonly enabled: boolean;
  readonly params: Readonly<Record<string, number | string>>;
}

export interface SmartObjectLayer extends LayerBase {
  readonly kind: 'smartObject';
  readonly source: EmbeddedSource;
  /**
   * 原始（未套滤镜）像素，与 `RasterLayer.tiles` 同一约定——非破坏性的
   * 关键：滤镜链只读它，从不改写它（M4-06 / ADR-0008）。
   */
  readonly raster: TileHandle;
  readonly bounds: Rect;
  /** 非破坏性滤镜链，按顺序应用。 */
  readonly filters: readonly SmartFilter[];
  /** 栅格化缓存。可为空——缓存丢了只是要重算，不影响正确性。 */
  readonly cache?: TileHandle;
}

export type Layer =
  | RasterLayer
  | GroupLayer
  | TextLayer
  | ShapeLayer
  | AdjustmentLayer
  | SmartObjectLayer
  | FillLayer;

/** 图层种类的字面量集合，用于穷尽性检查与测试。 */
export const LAYER_KINDS = [
  'raster',
  'group',
  'text',
  'shape',
  'adjustment',
  'smartObject',
  'fill',
] as const;

export type LayerKind = (typeof LAYER_KINDS)[number];

/* ------------------------------------------------------------------ */
/* 选区、参考线、文档                                                    */
/* ------------------------------------------------------------------ */

/**
 * 选区。
 *
 * §5.1 要求选区是**带羽化的 alpha 蒙版**而非路径——路径表达不了羽化与
 * 半透明选择。路径只是构造选区的一种输入。
 */
export interface SelectionState {
  /** 选区蒙版的像素，为空表示全选。 */
  readonly mask?: TileHandle;
  /** 选区外接矩形，**文档空间**像素。 */
  readonly bounds: Rect;
  /** 构造用的路径，保留以便再编辑。 */
  readonly path?: PathData;
}

export interface Guide {
  readonly axis: 'horizontal' | 'vertical';
  /** 位置，**文档空间**像素。 */
  readonly position: number;
  readonly locked: boolean;
}

/**
 * 已保存的路径（Paths 面板一条列表项，DESIGN.md §7.1 补充，参照 Photoshop）。
 *
 * 之前 `PathData` 只挂在 `ShapeLayer.path`/`vectorMask`/`SelectionState.path`
 * 上，都是从属于别的对象；这是第一个"独立存在、不属于任何图层"的路径，
 * 因此需要单独的载体而不是复用某个图层字段——ADR-0011（`state/DECISIONS.md`）
 * 记录了这个新字段的取舍。
 *
 * 坐标空间：跟 `compositeShape`（`app/composite.ts`）对形状图层路径的既有
 * 简化一致——直接当**文档空间**像素使用，不做图层 transform 那一层换算
 * （已保存路径本身也不属于任何图层，没有 transform 可言）。
 */
export interface SavedPath {
  readonly id: string;
  readonly name: string;
  readonly path: PathData;
}

export interface DocumentMeta {
  readonly title: string;
  /** 创建时间，ISO 8601。 */
  readonly createdAt: string;
  readonly modifiedAt: string;
  /** 原始导入文件名，用于"另存为"时给默认名。 */
  readonly sourceFileName?: string;
}

/**
 * 图层复合的一条图层快照（M4-05 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 * 只记"位置/可见性/不透明度/样式启用"四类可切换状态——不含像素、不含
 * 结构（子图层增删仍然对所有复合生效，这是验收标准 4）。
 */
export interface LayerCompEntry {
  readonly layerId: LayerId;
  readonly visible: boolean;
  readonly opacity: number;
  readonly transform: Mat3;
  /** 与该图层 `effects` 数组一一对应的启用状态；长度对不上时视为该图层已变化，跳过。 */
  readonly effectsEnabled: readonly boolean[];
}

export interface LayerComp {
  readonly id: string;
  readonly name: string;
  readonly entries: readonly LayerCompEntry[];
}

export interface Document {
  readonly id: string;
  /** 画布尺寸，像素。 */
  readonly size: { readonly width: number; readonly height: number };
  readonly colorMode: ColorMode;
  readonly bitDepth: BitDepth;
  /** 工作色彩空间。合成在这里进行，内部统一线性光。 */
  readonly workingSpace: ColorSpace;
  readonly dpi: number;
  /** 根组。图层树的入口。 */
  readonly root: GroupLayer;
  readonly selection: SelectionState;
  readonly guides: readonly Guide[];
  /** Paths 面板列表（DESIGN.md §7.1 补充）。 */
  readonly paths: readonly SavedPath[];
  readonly meta: DocumentMeta;
  /** 图层复合列表（M4-05）。 */
  readonly layerComps: readonly LayerComp[];
}
